import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { ExternalLink } from '@/atoms/ExternalLink'
import { Image } from '@/atoms/Image'
import { AsSpan, CaptionMD, CaptionSM, LabelSM, LabelXS, ParagraphSM, TitleXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { NamedColor } from '@/constants/types'
import { BoundedImage } from '@/molecules/BoundedImage'
import { useGuildMember } from '@/services/GuildUserService'
import { formatPrice } from '@/services/PriceFormatter'
import { Project } from '@/services/ProjectsService'
import {
  useShopifyCollection,
  buildUtmCampaign,
  buildUtmContext,
  MerchDiscount,
  buildProductUrl,
} from '@/services/Shopify/ShopifyService'
import { buildAnalyticsLink } from '@/utils/AnalyticsLinkBuilder'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

interface GiftsTabProps {
  storeUrl: string
  accessToken: string
  handle: string
  storeHref: string
  discount?: MerchDiscount
  isDarkMode?: boolean
  textColor?: NamedColor
  projectSlug?: string
  projectName?: string | null
  utm?: {
    campaign: string
    linkContext: string
  }
  analytics: {
    content_id?: string
    context: string
    program: string
  }
  projectData: Project
}

export const GiftsTab: React.FC<GiftsTabProps> = ({
  storeUrl,
  accessToken,
  handle,
  storeHref,
  projectSlug,
  discount,
  utm,
  analytics,
  isDarkMode = true,
  projectName,
  projectData,
}) => {
  const { asPath } = useSafeRouter()
  const { locale } = useLocale()
  const { t } = useTranslate('ads')
  const { data } = useShopifyCollection({ storeUrl, accessToken, handle })
  const { guildPermissions, isGuildMember } = useGuildMember()
  const { track } = useSafeAnalytics()
  const utmLinkContext = buildUtmContext(utm)
  const utmCampaign = buildUtmCampaign(utm)
  const hasMerchDiscount = Boolean(guildPermissions?.hasMerchDiscount)
  const isSpecial = Boolean(projectData?.projectType === 'special')
  const joinHref =
    isGuildMember && storeHref ? storeHref : projectSlug ? `${paths.guild.join}/${projectSlug}` : paths.guild.join
  const visitTheAngelGiftShop = isSpecial
    ? t('visitTheDryBarComedyGiftShop', 'Visit the Dry Bar Comedy Gift Shop')
    : storeHref
    ? t('visitTheStoreGiftShop', 'Visit the {{ projectName }} Gift Shop', {
        projectName,
      })
    : t('visitTheAngelGiftShop', 'Visit the Angel Gift Shop')
  const cardLogoImageUrl = isSpecial
    ? getCloudinaryImageUrl({ path: '/v1739221751/studio-app/catalog/d026ed61-de35-440c-9a02-af49fcf35e4c' })
    : projectData?.title?.logo?.cloudinaryPath ?? projectData?.logoCloudinaryPath

  const massaged =
    data?.collection?.products?.edges
      .map(({ node }) => {
        return {
          id: node.id,
          minPrice: {
            value: node.priceRange.minVariantPrice.amount,
            currency: node.priceRange.minVariantPrice.currencyCode,
          },
          compareAtMinPrice: {
            value: node.compareAtPriceRange.minVariantPrice.amount,
            currency: node.compareAtPriceRange.minVariantPrice.currencyCode,
          },
          title: node.title,
          url: buildProductUrl(node, hasMerchDiscount, discount),
          imageUrl: node.featuredImage?.url,
        }
      })
      .filter((item) => item?.url && item?.imageUrl && item?.title)
      .slice(0, 10) || []

  return (
    <div id="gifts-section" className="grid grid-cols-2 gap-4 md:flex md:flex-row md:flex-wrap">
      <button
        className="relative flex max-h-full w-full max-w-[300px] shrink-0 cursor-pointer snap-center snap-always items-center justify-center overflow-hidden rounded-xl bg-core-gray-900 p-4 sm:w-2/5 md:w-2/3 md:min-w-[256px] lg:w-3/5"
        onClick={() => {
          track('Clicked Guild Join Merch Rail', analytics)
          window.location.href = joinHref
        }}
        aria-labelledby="join-guild-for-discounts"
      >
        <div className="relative flex flex-col items-center justify-center gap-4 text-center md:gap-6 lg:gap-[26px]">
          <Image
            src={getCloudinaryImageUrl({
              path: '/v1719424983/angel-studios/merch/guild/Angel_Guild_logo_arc.png',
              transforms: 'e_trim',
              height: 82,
              width: 66,
            })}
            alt="guild"
            width={65}
            height={82}
            className="max-h-[32px] max-w-[26px] md:max-h-[64px] md:max-w-[51px] lg:max-h-[82px] lg:max-w-[66px]"
          />
          <div className="flex flex-col items-center gap-2">
            <Image
              src={getCloudinaryImageUrl({
                path: '/v1727813059/angel-studios/merch/guild/20-p.webp',
                transforms: 'e_trim,q_auto:best',
                height: 255,
                width: 516,
              })}
              className="max-h-[32px] max-w-[78px] md:max-h-[40px] md:max-w-[98px] lg:max-h-[45px] lg:max-w-[125px]"
              alt="discount"
              width={125}
              height={45}
            />
            <TitleXS color="core-gray-400" className="md:photon-title-sm">
              {t('allMerchandise', 'all merchandise')}
            </TitleXS>
          </div>
          {!hasMerchDiscount && (
            <LinkButton
              href={joinHref}
              variant={isDarkMode ? 'core-gray-700' : 'lightgray'}
              outline={!isDarkMode}
              id="join-guild-for-discounts"
              className={classNames('stroke-white !px-4 !py-2 md:!px-5 md:!py-2.5 text-sm hover:stroke-core-gray-950', {
                'text-white border-0': isDarkMode,
              })}
              onClick={(e) => {
                e.stopPropagation()
                track('Clicked Guild Join Merch Rail', analytics)
              }}
            >
              <LabelXS>{t('joinForDiscounts', 'Join for Discounts')}</LabelXS>
            </LinkButton>
          )}
        </div>
      </button>
      {massaged?.map((item, index) => {
        const minPrice = formatPrice(item.minPrice.value, {
          includeDecimals: true,
          currency: item.minPrice.currency,
          locale: locale,
          skipDecimalMath: true,
        })
        const compareAtMinPrice = formatPrice(item.compareAtMinPrice.value, {
          includeDecimals: true,
          currency: item.compareAtMinPrice.currency,
          locale: locale,
          skipDecimalMath: true,
        })

        return (
          <div
            className="w-full max-w-[300px] shrink-0 snap-center snap-always sm:w-2/5 md:w-2/3 md:min-w-[256px] lg:w-3/5"
            key={item.id}
          >
            <div>
              <ExternalLink
                href={buildAnalyticsLink({
                  href: item.url,
                  path: asPath,
                  campaign: utmCampaign,
                  linkContext: utmLinkContext,
                })}
                onClick={() => {
                  track('Clicked Merch Item', {
                    ...analytics,
                    itemName: item.title,
                    itemUrl: item.url,
                    itemPosition: index,
                  })
                }}
              >
                <div className="rounded-xl bg-white p-2 md:p-4">
                  <div className="aspect-h-[180] aspect-w-[248] relative w-full rounded-lg bg-white">
                    <div className="flex w-full items-center justify-center">
                      {discount && (
                        <CaptionMD
                          color="black"
                          className="absolute right-4 top-4 rounded-full border border-oxide-200 bg-oxide-100 px-3 py-1"
                        >
                          {t('savePercentMerch', 'Save {{percent}}%', {
                            percent: Math.floor(discount?.value * 100),
                          })}
                        </CaptionMD>
                      )}
                      <img alt={item.title} src={item.imageUrl} className="mb-2 max-h-full w-auto" loading="lazy" />
                    </div>
                  </div>
                  <CaptionSM
                    className="md:photon-paragraph-sm lg:photon-paragraph-md mb-4 overflow-x-hidden text-ellipsis whitespace-nowrap !text-base"
                    weight="medium"
                    color={'black'}
                  >
                    {item.title}
                  </CaptionSM>
                  <div className="flex min-h-[52px] items-center">
                    <div className="flex flex-col-reverse items-baseline gap-0 md:flex-row md:gap-2">
                      <ParagraphSM
                        color={isDarkMode ? 'core-gray-950' : 'core-gray-700'}
                        as={AsSpan}
                        weight="bold"
                        className="md:photon-paragraph-lg leading-0"
                      >
                        {minPrice}
                      </ParagraphSM>
                      <When condition={minPrice < compareAtMinPrice}>
                        <ParagraphSM
                          as={AsSpan}
                          color="core-gray-700"
                          weight="bold"
                          className="md:photon-paragraph-lg leading-0 -mb-2 line-through md:mb-0"
                        >
                          {compareAtMinPrice}
                        </ParagraphSM>
                      </When>
                    </div>
                    <div className="ml-auto">
                      <Button
                        variant={isDarkMode ? 'core-gray-900' : 'black'}
                        className={classNames(
                          'flex items-center py-2 px-4 lg:px-6 lg:py-[14px]',
                          isDarkMode && '!border-0 !bg-core-gray-950',
                        )}
                      >
                        <LabelSM weight="semibold" color="white" className="cursor-pointer">
                          {t('buy', 'Buy')}
                        </LabelSM>
                      </Button>
                    </div>
                  </div>
                </div>
              </ExternalLink>
            </div>
          </div>
        )
      })}
      <button
        className="relative mr-4 flex max-h-full w-full max-w-[300px] shrink-0 cursor-pointer snap-center snap-always items-center justify-center overflow-hidden rounded-xl bg-core-gray-900 p-4 sm:w-2/5 md:w-2/3 md:min-w-[256px] lg:w-3/5"
        onClick={() => {
          track('Clicked View All Merch', analytics)
          window.location.href = storeHref
        }}
        aria-labelledby="join-guild-for-discounts"
      >
        <div className="relative flex flex-col items-center justify-center gap-4 text-center md:gap-6 lg:gap-[26px]">
          <BoundedImage
            className={classNames(
              'mx-auto flex items-center justify-center pb-4 lg:-mt-2 transition-opacity duration-300 ease-in-out will-change-opacity',
            )}
            alt={projectData?.name}
            path={cardLogoImageUrl}
            width={200}
            height={36}
            sizes="(max-width: 1024px) 30px, (max-width: 1280px) 50px, 50px"
            imageClassName="!max-w-[210px] lg:!max-w-[250px] max-h-[35px] md:!max-h-[45px] lg:!max-h-[60px] lg:max-w-fit shortDesktop:!max-h-[40px]"
          />
          <div className="flex flex-col items-center gap-2">
            <TitleXS color="core-gray-400" className="md:photon-title-sm text-pretty">
              {visitTheAngelGiftShop}
            </TitleXS>
          </div>
          <LinkButton
            href={joinHref}
            variant={'core-gray-700'}
            outline={!isDarkMode}
            id="join-guild-for-discounts"
            className={classNames('stroke-white !px-5 !py-2.5 bg-core-gray-700/60 text-sm hover:stroke-core-gray-950', {
              'text-white border-0': isDarkMode,
            })}
            onClick={() => {
              track('Clicked View All Merch', analytics)
              window.location.href = storeHref
            }}
          >
            <LabelXS>{t('viewAllMerchandise', 'View All Merchandise')}</LabelXS>
          </LinkButton>
        </div>
      </button>
    </div>
  )
}
